import { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { EyeBlueIcon } from "../../../../../../assets";
import { OrganisationTypeConstants } from "../../../../../../constants";
import { CursorChangeProps, Origin, ResultType } from "../../../../../../models";
import {
  getWorkflowTemplate,
  GetWorkflowTemplateResponse,
  searchProjectTracker,
  SearchProjectTrackerResponse,
} from "../../../../../../service/query";
import { ResultData, Status } from "../../../../../../service/Shared";
import { useAuth } from "../../../../../../useAuth";
import { flattenObject } from "../../../../../../utils";
import { getProjectDetailsByUuid, getWorkflowTasksBoardRoute } from "../../../../../../utils/routes";
import {
  DataGridColumnDefinition,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
  Toast,
} from "../../../../../../widget";
import { IconCellFormatterData } from "../../../../../../widget/data/DataGrid/models";

interface UseRelatedProjectsPanelProps {
  workflowTemplateUuid?: string;
}

interface UseRelatedProjectsPanelReturnData {
  relatedProjectsPanelRef: RefObject<HTMLDivElement>;
  workflowTemplate: GetWorkflowTemplateResponse | undefined;
  showApplyWorkflowTemplatePanel: boolean;
  setShowApplyWorkflowTemplatePanel: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  refresh: boolean;
  columns: DataGridColumnDefinition[];
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
  currentUserType: OrganisationTypeConstants;
}

const columns: DataGridColumnDefinition[] = [
  {
    name: "Name",
    key: "projectLink",
    dataType: "string",
    formatter: "link",
    filterable: true,
    sortable: true,
  },
  {
    name: "Developer",
    key: "developer.displayName",
    dataType: "string",
    filterable: true,
    sortable: true,
  },
  {
    name: "Country",
    key: "addressCountryCode",
    dataType: "string",
    formatter: "countryByCode",
    filterable: false,
    sortable: false,
  },
  {
    name: "Area (ha)",
    key: "areaNetHa",
    dataType: "number",
    filterable: true,
    sortable: true,
  },
  {
    name: "Duration",
    key: "duration",
    dataType: "number",
    filterable: true,
    sortable: true,
  },
  {
    name: "Current phase",
    key: "cachedPhase",
    dataType: "string",
    filterable: true,
    sortable: true,
  },
  {
    name: "View",
    key: "viewLink",
    dataType: "string",
    formatter: "icon",
    alignment: "center",
    filterable: false,
    sortable: false,
    minWidth: 66,
  },
];

const pageSize = 10;

export const useRelatedProjectPanel = ({
  workflowTemplateUuid,
}: UseRelatedProjectsPanelProps): UseRelatedProjectsPanelReturnData => {
  const navigate = useNavigate();
  const { currentUserType } = useAuth();

  const relatedProjectsPanelRef = useRef<HTMLDivElement>(null);

  const [showApplyWorkflowTemplatePanel, setShowApplyWorkflowTemplatePanel] = useState(false);
  const [workflowTemplate, setWorkflowTemplate] = useState<GetWorkflowTemplateResponse | undefined>();

  const [isLoading, setIsLoading] = useState(true);
  const [refresh, setRefresh] = useState(true);

  const formatData = useCallback(
    (responseData: SearchProjectTrackerResponse | undefined): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el);

        result.projectLink = {
          text: el.displayName,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, prettier/prettier
          to: el.uuid ? getProjectDetailsByUuid(el.uuid, currentUserType, { origin: Origin.WorkflowsPage }) : "",
        } as DataGridLinkCellFormatterData;

        result.viewLink = {
          action: () => {
            if (el.workflow.uuid) navigate(getWorkflowTasksBoardRoute(el.workflow.uuid, currentUserType));
          },
          icon: <EyeBlueIcon width={20} height={20} strokeWidth={1.5} />,
        } as IconCellFormatterData;
        return result;
      }) || [],
    []
  );

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    setIsLoading(true);

    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    if (filterCriteria.projectLink) {
      filterCriteria.displayName = filterCriteria.projectLink;
      delete filterCriteria.projectLink;
    }

    filterCriteria.workflow = {
      templateUuid: {
        operator: "eq",
        value: workflowTemplateUuid,
      },
    };

    const sortKeyMap: Record<string, string> = {
      projectLink: "displayName",
    };

    await searchProjectTracker({
      paging: {
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
        limit: pageSize,
      },
      /* eslint-disable @typescript-eslint/no-explicit-any */
      sort: sorting.map((s: { key: any; direction: any }) => ({
        key: sortKeyMap[s.key] || s.key,
        direction: s.direction,
      })),
      /* eslint-enable */
      filter: { results: filterCriteria },
    }).then(async (response) => {
      data = {
        resultData: formatData(response.data),
        paging: {
          startCursor: response.data?.paging?.startCursor || "",
          endCursor: response.data?.paging?.endCursor || "",
          pageSize,
          totalCount: response.data?.paging?.total || 0,
          hasNextPage: response.data?.paging?.hasNextPage || false,
          hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
        },
      };
    });

    setIsLoading(false);
    return data;
  };

  const getAndSetWorkflowTemplate = useCallback(
    async (uuid: string) => {
      const res = await getWorkflowTemplate({
        workflowTemplateUuid: uuid,
      });
      if (res.status === Status.Success && res.data) {
        setWorkflowTemplate(res.data);
      }
      if (res.status === Status.Error && res.errors) {
        Toast.error(res.errors[0]);
      }
    },
    [workflowTemplateUuid]
  );

  useEffect(() => {
    if (workflowTemplateUuid) {
      setRefresh((prev) => !prev);
      getAndSetWorkflowTemplate(workflowTemplateUuid);
      relatedProjectsPanelRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [workflowTemplateUuid]);

  return {
    relatedProjectsPanelRef,
    workflowTemplate,
    showApplyWorkflowTemplatePanel,
    setShowApplyWorkflowTemplatePanel,
    isLoading,
    refresh,
    columns,
    onChange,
    currentUserType,
  };
};
